import { type DealTrackingMetric } from "@vega-api/generated/types/dealTrackingMetric";
import { MetricCard } from "@vega-core/components/MetricCard/MetricCard";
import { m } from "@vega-core/paraglide/messages";

import {
  getMetricDisplayValue,
  METRIC_CARD_LOADING_PLACEHOLDERS,
} from "../../utils/dealTrackingMetricDisplay";

import { ReportSectionMessage } from "./ReportSectionMessage";

type DealOutcomeMetricsProps = {
  isError: boolean;
  isLoading: boolean;
  metrics: DealTrackingMetric[];
  onRetry: () => void;
};

export function DealOutcomeMetrics({
  isError,
  isLoading,
  metrics,
  onRetry,
}: DealOutcomeMetricsProps) {
  if (isError) {
    return (
      <div className="space-y-4">
        <h2 className="heading-5">{m.coInvestments_report_dealOutcome()}</h2>
        <ReportSectionMessage
          dataTestId="deal-outcome-error"
          message={m.common_error_somethingWentWrong()}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (!isLoading && metrics.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="heading-5">{m.coInvestments_report_dealOutcome()}</h2>
        <ReportSectionMessage
          dataTestId="deal-outcome-empty"
          message={m.coInvestments_report_dealOutcome_noData()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="heading-5">{m.coInvestments_report_dealOutcome()}</h2>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="report-metrics-grid"
      >
        {isLoading
          ? METRIC_CARD_LOADING_PLACEHOLDERS.map((id) => (
              <MetricCard
                dataTestId={`report-metric-loading-${id}`}
                isLoading
                key={id}
                title=""
                value=""
              />
            ))
          : metrics.map((metric) => (
              <MetricCard
                dataTestId={`report-metric-${metric.label}`}
                key={metric.label}
                periodContent={
                  metric.description ? (
                    <p className="body-small-regular text-text/secondary">
                      {metric.description}
                    </p>
                  ) : undefined
                }
                title={metric.label}
                value={getMetricDisplayValue(metric)}
              />
            ))}
      </div>
    </div>
  );
}
